import type { ConcreteClass, RcDesignCode, SteelGrade } from "../types/input";

/**
 * Betonarme kesit tasarımı profilleri: TS 500-2000 · EN 1992-1-1 (EC2) · ACI 318-19.
 * Ortak: dikdörtgen gerilme bloğu ile tek sıra çekme donatılı dikdörtgen kesit; kesme donatısız plak/perde sınırı;
 * düz çubuk kenetlenme boyu; yük kombinasyon çarpanları (G kalıcı, Q hareketli, H toprak itkisi, F su, E deprem).
 * Birimler: gerilme MPa, uzunluk m, kuvvet kN, alan mm²/m (görüntü kolaylığı).
 */
export interface RcMaterials {
  concrete: ConcreteClass; steel: SteelGrade;
  fck: number; fyk: number;            // MPa
  fcd: number; fyd: number;            // tasarım (ACI'de fck, fyk — katsayı φ dirençte)
  fctd: number;                        // çekme tasarım dayanımı (TS: 0,35√fck/1,5; EC2: 0,7·fctm/1,5; ACI: 0,17√fc' eşdeğeri, yalnız bilgi)
  fctm?: number;
  Es: number;                          // 200 000 MPa
  /** Gerilme bloğu: eşdeğer basınç gerilmesi (MPa) ve blok derinliği oranı (a = k₁·c) */
  sigmaBlock: number; k1: number;
  /** Direnç katsayısı (ACI φ; TS/EC2: 1) */
  phiFlexure: number; phiShear: number;
}
export interface RcCombination { id: string; label: string; kind: "static" | "seismic"; G: number; Q: number; H: number; F: number; E: number }
export interface RcMinRatios {
  /** Ana eğilme donatısı için: max(ρ₁·b·d, ρ₂·b·h) */
  flexuralBd: number; flexuralBh: number;
  /** Gövde ikincil yüz düşey (b·h oranı, tek yüz), yatay toplam (iki yüz), taban dağıtma toplam */
  stemVerticalFace: number; stemHorizontalTotal: number; baseDistributionTotal: number;
  /** Dağıtma donatısı ana donatının en az bu oranı */
  distributionOfMain: number;
}
export interface RcCodeProfile {
  id: RcDesignCode; label: string; references: string[];
  materials(concrete: ConcreteClass, steel: SteelGrade, opts?: { alphaCc?: number }): RcMaterials;
  combinations(seismic: boolean, psi2: number): RcCombination[];
  min: RcMinRatios;
  /** En büyük donatı aralığı (m): ana ve ikincil/dağıtma; h kesit kalınlığı (m) */
  maxSpacing(h: number): { main: number; secondary: number };
  /** Süneklik sınırı: c/d ≤ … (TS 500: ρ ≤ 0,85·ρb ile eşdeğer; EC2: 0,45; ACI: 0,375 → εt ≥ 0,005) */
  maxNeutralAxisRatio(m: RcMaterials): number;
  /** Kesme donatısız plak/perde kesme dayanımı (kN/m); ρ boyuna donatı oranı (As/(b·d)) */
  shearResistance(m: RcMaterials, b: number, d: number, rho: number): { Vr: number; formula: string };
  /** Düz çubuk kenetlenme boyu (m), çekme, iyi aderans */
  developmentLength(m: RcMaterials, dia: number): { lb: number; formula: string };
  /** Kombinasyon açıklaması (rapor) */
  loadFactorNote: string;
}

export const CONCRETE_FCK: Record<ConcreteClass, number> = { C20: 20, C25: 25, C30: 30, C35: 35, C40: 40, C45: 45, C50: 50 };
export const STEEL_FYK: Record<SteelGrade, number> = { B420C: 420, B500C: 500 };
const ES = 200_000;

/** TS 500 Tablo 3.x / §7.1: k₁ = 0,85 (≤ C25) … 0,70 (C50) */
export function ts500k1(fck: number): number {
  return Math.max(0.7, Math.min(0.85, 0.85 - 0.006 * (fck - 25)));   // C30 0,82 · C35 0,79 · C40 0,76 · C45 0,73 · C50 0,70
}
/** ACI 318-19 Tablo 22.2.2.4.3: β₁ = 0,85 (≤ 28 MPa), −0,05 her 7 MPa, ≥ 0,65 */
export function aciBeta1(fc: number): number {
  return Math.max(0.65, Math.min(0.85, 0.85 - (0.05 * (fc - 28)) / 7));
}

const ts500: RcCodeProfile = {
  id: "ts500", label: "TS 500-2000",
  references: ["TS 500 (Şubat 2000) — Betonarme Yapıların Tasarım ve Yapım Kuralları: §6.2.6 yük kombinasyonları, §7.1–7.3 eğilme, §8.1 kesme, §9.1 kenetlenme, §11.4.5 döşeme donatı sınırları"],
  materials(concrete, steel) {
    const fck = CONCRETE_FCK[concrete], fyk = STEEL_FYK[steel];
    const fcd = fck / 1.5, fyd = fyk / 1.15, fctd = (0.35 * Math.sqrt(fck)) / 1.5;
    return { concrete, steel, fck, fyk, fcd, fyd, fctd, Es: ES, sigmaBlock: 0.85 * fcd, k1: ts500k1(fck), phiFlexure: 1, phiShear: 1 };
  },
  combinations(seismic, _psi2) {
    return seismic
      ? [{ id: "TS-E", label: "G + Q + H + F + E", kind: "seismic", G: 1, Q: 1, H: 1, F: 1, E: 1 }]
      : [{ id: "TS-1", label: "1,4G + 1,6Q + 1,6H + 1,4F", kind: "static", G: 1.4, Q: 1.6, H: 1.6, F: 1.4, E: 0 },
        { id: "TS-2", label: "0,9G + 1,6H + 1,4F", kind: "static", G: 0.9, Q: 0, H: 1.6, F: 1.4, E: 0 }];
  },
  // §7.3 ρmin = 0,8·fctd/fyd (b·d) ve §11.4.5 döşeme 0,002·b·h (S420); perde ikincil 0,0015 yüz başına yaklaşık → 0,001 (TBDY 7.6.3'ün yarısı), yatay 0,0025 toplam
  min: { flexuralBd: NaN, flexuralBh: 0.002, stemVerticalFace: 0.001, stemHorizontalTotal: 0.0025, baseDistributionTotal: 0.002, distributionOfMain: 0.2 },
  maxSpacing(h) { return { main: Math.min(1.5 * h, 0.2), secondary: 0.3 }; },
  maxNeutralAxisRatio(m) { return 0.85 * (600 / (600 + m.fyd)); },   // ρ ≤ 0,85·ρb ⇔ c/d ≤ 0,85·cb/d
  shearResistance(m, b, d) {
    const Vcr = 0.65 * m.fctd * 1000 * b * d;                       // MPa → kN/m²
    return { Vr: Vcr, formula: "V_cr = 0,65·f_ctd·b·d (TS 500 Denk. 8.1, N_d = 0; kesme donatısız döşeme/temel: V_d ≤ V_cr)" };
  },
  developmentLength(m, dia) {
    const lb = Math.max(0.12 * (m.fyd / m.fctd), 20) * (dia / 1000);
    return { lb, formula: "l_b = 0,12·(f_yd/f_ctd)·φ ≥ 20φ (TS 500 Denk. 9.1)" };
  },
  loadFactorNote: "TS 500 §6.2.6: toprak itkisi H = 1,6; su F = 1,4; depremli durumda tüm çarpanlar 1,0.",
};

const ec2: RcCodeProfile = {
  id: "ec2", label: "EN 1992-1-1",
  references: ["EN 1992-1-1:2004 — Eurocode 2: §3.1.6 fcd, §3.2.7 fyd, §6.1 eğilme, §6.2.2 kesme (VRd,c), §8.4 kenetlenme, §9.3 döşeme ve §9.6 perde donatı sınırları", "EN 1990 Ek A1 kombinasyonlar (γG 1,35 / γQ 1,5; deprem: ψ₂)"],
  materials(concrete, steel, opts) {
    const fck = CONCRETE_FCK[concrete], fyk = STEEL_FYK[steel];
    const alphaCc = opts?.alphaCc ?? 1.0;
    const fcd = (alphaCc * fck) / 1.5, fyd = fyk / 1.15;
    const fctm = 0.3 * fck ** (2 / 3), fctd = (0.7 * fctm) / 1.5;
    return { concrete, steel, fck, fyk, fcd, fyd, fctd, fctm, Es: ES, sigmaBlock: fcd, k1: 0.8, phiFlexure: 1, phiShear: 1 };   // η = 1, λ = 0,8 (≤ C50)
  },
  combinations(seismic, psi2) {
    return seismic
      ? [{ id: "EC-E", label: `G + ${psi2.toLocaleString("tr-TR")}·Q + H + F + E`, kind: "seismic", G: 1, Q: psi2, H: 1, F: 1, E: 1 }]
      : [{ id: "EC-1", label: "1,35G + 1,5Q + 1,35H + 1,35F", kind: "static", G: 1.35, Q: 1.5, H: 1.35, F: 1.35, E: 0 },
        { id: "EC-2", label: "1,0G + 1,5Q + 1,35H + 1,35F", kind: "static", G: 1.0, Q: 1.5, H: 1.35, F: 1.35, E: 0 }];
  },
  // §9.2.1.1 As,min = max(0,26·fctm/fyk, 0,0013)·b·d → fctm'e bağlı; flexuralBd profilde hesaplanır (materials ile); §9.6.2 perde düşey 0,002·Ac toplam
  min: { flexuralBd: NaN, flexuralBh: 0, stemVerticalFace: 0.001, stemHorizontalTotal: 0.002, baseDistributionTotal: 0.002, distributionOfMain: 0.2 },
  maxSpacing(h) { return { main: Math.min(3 * h, 0.4), secondary: Math.min(3.5 * h, 0.45) }; },
  maxNeutralAxisRatio() { return 0.45; },
  shearResistance(m, b, d, rho) {
    const k = Math.min(2, 1 + Math.sqrt(0.2 / d));                    // d (m): √(200/d[mm])
    const rhoL = Math.min(rho, 0.02);
    const CRdc = 0.18 / 1.5;
    const vMin = 0.035 * k ** 1.5 * Math.sqrt(m.fck);
    const v = Math.max(CRdc * k * Math.cbrt(100 * rhoL * m.fck), vMin);
    return { Vr: v * 1000 * b * d, formula: "V_Rd,c = max[C_Rd,c·k·(100·ρ_l·f_ck)^⅓, v_min]·b·d (EN 1992-1-1 Denk. 6.2a/b)" };
  },
  developmentLength(m, dia) {
    const eta2 = dia <= 32 ? 1 : (132 - dia) / 100;
    const fbd = 2.25 * 1.0 * eta2 * m.fctd;
    const lbRqd = (dia / 1000 / 4) * (m.fyd / fbd);
    const lb = Math.max(lbRqd, 0.3 * lbRqd, 10 * (dia / 1000), 0.1);
    return { lb, formula: "l_bd = (φ/4)·(f_yd/f_bd), f_bd = 2,25·η₁·η₂·f_ctd (EN 1992-1-1 §8.4.3–8.4.4, α₁…α₅ = 1)" };
  },
  loadFactorNote: "EN 1990 Denk. 6.10: γG = 1,35 (elverişsiz) / 1,0 (elverişli), γQ = 1,5; toprak itkisi kalıcı etki; deprem: G + ψ₂·Q + E.",
};

const aci318: RcCodeProfile = {
  id: "aci318", label: "ACI 318-19",
  references: ["ACI 318-19 — Building Code Requirements for Structural Concrete: §5.3 yük kombinasyonları, §22.2 eğilme (β₁, φ = 0,9), §22.5.5 Vc = 0,17·λ·√f'c·b·d (φ = 0,75), §25.4.2 kenetlenme, §7.6/§11.6 döşeme ve duvar minimum donatı"],
  materials(concrete, steel) {
    const fck = CONCRETE_FCK[concrete], fyk = STEEL_FYK[steel];
    return { concrete, steel, fck, fyk, fcd: fck, fyd: fyk, fctd: 0.17 * Math.sqrt(fck), Es: ES, sigmaBlock: 0.85 * fck, k1: aciBeta1(fck), phiFlexure: 0.9, phiShear: 0.75 };
  },
  combinations(seismic, _psi2) {
    return seismic
      ? [{ id: "ACI-5", label: "1,2D + 1,0E + 1,0L + 1,6H", kind: "seismic", G: 1.2, Q: 1.0, H: 1.6, F: 1.2, E: 1.0 },
        { id: "ACI-7", label: "0,9D + 1,0E + 1,6H", kind: "seismic", G: 0.9, Q: 0, H: 1.6, F: 1.2, E: 1.0 }]
      : [{ id: "ACI-2", label: "1,2D + 1,6L + 1,6H + 1,2F", kind: "static", G: 1.2, Q: 1.6, H: 1.6, F: 1.2, E: 0 },
        { id: "ACI-6", label: "0,9D + 1,6H + 1,2F", kind: "static", G: 0.9, Q: 0, H: 1.6, F: 1.2, E: 0 }];
  },
  // §7.6.1.1 / §8.6.1.1 döşeme ve temel As,min = 0,0018·b·h (fy = 420); §11.6.1 duvar ρl ≥ 0,0015, ρt ≥ 0,0025 (tutucu üst değerler)
  min: { flexuralBd: 0, flexuralBh: 0.0018, stemVerticalFace: 0.00075, stemHorizontalTotal: 0.0025, baseDistributionTotal: 0.0018, distributionOfMain: 0 },
  maxSpacing(h) { return { main: Math.min(3 * h, 0.45), secondary: Math.min(3 * h, 0.45) }; },
  maxNeutralAxisRatio() { return 0.375; },                           // εt ≥ 0,005 (çekme kontrollü, φ = 0,9)
  shearResistance(m, b, d) {
    const Vc = 0.17 * Math.sqrt(m.fck) * 1000 * b * d;
    return { Vr: 0.75 * Vc, formula: "φV_c = 0,75·0,17·λ·√f'c·b·d (ACI 318-19 Tablo 22.5.5.1, λ = 1)" };
  },
  developmentLength(m, dia) {
    const div = dia <= 19 ? 2.1 : 1.7;
    const ld = Math.max((m.fyk / (div * Math.sqrt(m.fck))) * (dia / 1000), 0.3);
    return { lb: ld, formula: "l_d = f_y·ψ_t·ψ_e·ψ_g·d_b/(2,1·λ·√f'c) (≤ φ19) ya da /(1,7·λ·√f'c) ≥ 300 mm (ACI 318-19 Tablo 25.4.2.3)" };
  },
  loadFactorNote: "ACI 318-19 §5.3.1: 1,2D + 1,6L + 1,6H; 0,9D + 1,6H; deprem: 1,2D + 1,0E + 1,0L + 1,6H ve 0,9D + 1,0E + 1,6H.",
};

export const RC_PROFILES: Record<RcDesignCode, RcCodeProfile> = { ts500, ec2, aci318 };

/** Ana eğilme donatısı alt sınırı (mm²/m): profil oranları + EC2/TS 500'ün fctd/fctm'e bağlı terimleri */
export function minFlexuralArea(p: RcCodeProfile, m: RcMaterials, b: number, d: number, h: number): number {
  let byBd = 0;
  if (p.id === "ts500") byBd = (0.8 * m.fctd) / m.fyd;
  else if (p.id === "ec2") byBd = Math.max((0.26 * (m.fctm ?? 0)) / m.fyk, 0.0013);
  return Math.max(byBd * b * d, p.min.flexuralBh * b * h) * 1e6;
}

/** designCode (checks) → betonarme profili: TBDY → TS 500, EC7 → EC2, ABD → ACI 318 */
export function rcCodeFor(code: "auto" | RcDesignCode, designCode: "tbdy2018" | "ec7" | "us"): RcDesignCode {
  if (code !== "auto") return code;
  return designCode === "ec7" ? "ec2" : designCode === "us" ? "aci318" : "ts500";
}
